// src/components/CampaignWizard/StepSchedule.tsx
import React, { useMemo } from "react";
import {
	Row,
	Col,
	FormGroup,
	Label,
	Input,
	FormText,
} from "reactstrap";
import Flatpickr from "react-flatpickr";

export type Recurrence = {
	freq: "" | "DAILY" | "WEEKLY" | "MONTHLY";
	interval: number;
	byWeekDays: string[];
	byMonthDay: number | null;
	endDate: Date | null;
};

export interface StepScheduleProps {
	data: {
		immediate: boolean;
		scheduledTime: Date | null;
		localTime: boolean;
		recurrence: Recurrence | null;
	};
	onChange: (d: {
		immediate: boolean;
		scheduledTime: Date | null;
		localTime: boolean;
		recurrence: Recurrence | null;
	}) => void;
	onNext: () => void;
	onBack: () => void;
}

const StepSchedule: React.FC<StepScheduleProps> = ({
	data,
	onChange,
	onNext,
	onBack,
}) => {
	// 1) Always prepare a non-null recurrence object
	const rec: Recurrence = data.recurrence ?? {
		freq: "",
		interval: 1,
		byWeekDays: [],
		byMonthDay: null,
		endDate: null,
	};

	// 2) Destructure from the safe `rec`
	const { freq, interval, byWeekDays, byMonthDay, endDate } = rec;

	// 3) Compute a little human-readable summary
	const recurrenceSummary = useMemo(() => {
		switch (freq) {
			case "DAILY":
				return `Repeats every ${interval} day(s).`;
			case "WEEKLY":
				if (!byWeekDays.length)
					return `Repeats every ${interval} week(s).`;
				return `Repeats every ${interval} week(s) on ${byWeekDays.join(
					", "
				)}.`;
			case "MONTHLY":
				return `Repeats every ${interval} month(s) on day ${byMonthDay}.`;
			default:
				return "";
		}
	}, [freq, interval, byWeekDays, byMonthDay]);

	return (
		<>
			{/* ── Immediate toggle ────────────────────────── */}
			<Row className="mb-4">
				<Col>
					<FormGroup check switch>
						<Input
							type="switch"
							id="send-immediate-switch"
							checked={data.immediate}
							onChange={() =>
								onChange({ ...data, immediate: !data.immediate })
							}
						/>
						<Label for="send-immediate-switch" className="ms-2">
							Send Immediately
						</Label>
						<FormText color="muted">
							If enabled, this campaign goes out right away.
						</FormText>
					</FormGroup>
				</Col>
			</Row>

			{!data.immediate && (
				<>
					{/* ── Date & time picker ─────────────────────── */}
					<Row className="mb-4">
						<Col md={6}>
							<FormGroup>
								<Label for="scheduled-time"><strong>Scheduled Time</strong></Label>
								<Flatpickr
									id="scheduled-time"
									className="form-control"
									options={{
										dateFormat: "Y-m-d H:i",
										enableTime: true,
										time_24hr: true,
									}}
									value={data.scheduledTime ?? undefined}
									onChange={([d]) =>
										onChange({ ...data, scheduledTime: d })
									}
								/>
								<FormText color="muted">
									Choose when to send this campaign.
								</FormText>
							</FormGroup>

							{/* ── Local-time toggle ───────────────────── */}
							<FormGroup check className="mb-4">
								<Input
									type="checkbox"
									id="local-time-checkbox"
									checked={data.localTime}
									onChange={() =>
										onChange({ ...data, localTime: !data.localTime })
									}
								/>
								<Label for="local-time-checkbox" className="ms-2">
									Use recipient’s local time
								</Label>
								<FormText color="muted">
									Adjust send time for each recipient’s timezone.
								</FormText>
							</FormGroup>

							{/* ── Recurrence frequency ─────────────────── */}
							<FormGroup className="mb-3">
								<Label for="recurrence-freq"><strong>Repeat</strong></Label>
								<Input
									type="select"
									id="recurrence-freq"
									value={freq}
									onChange={(e) =>
										onChange({
											...data,
											recurrence: {
												freq: e.target.value as Recurrence["freq"],
												interval: 1,
												byWeekDays: [],
												byMonthDay: null,
												endDate: null,
											},
										})
									}
								>
									<option value="">No repeat</option>
									<option value="DAILY">Daily</option>
									<option value="WEEKLY">Weekly</option>
									<option value="MONTHLY">Monthly</option>
								</Input>
								<FormText color="muted">
									How often should this campaign repeat?
								</FormText>
							</FormGroup>

							{/* ── Daily settings ───────────────────────── */}
							{freq === "DAILY" && (
								<FormGroup className="d-flex align-items-center gap-2 mb-3">
									<Label className="mb-0">Every</Label>
									<Input
										type="number"
										min={1}
										style={{ width: 80 }}
										value={interval}
										onChange={(e) =>
											onChange({
												...data,
												recurrence: {
													...rec,
													interval: parseInt(e.target.value, 10) || 1,
												},
											})
										}
									/>
									<span>day(s)</span>
								</FormGroup>
							)}

							{/* ── Weekly settings ──────────────────────── */}
							{freq === "WEEKLY" && (
								<>
									<FormGroup className="d-flex align-items-center gap-2 mb-2">
										<Label className="mb-0">Every</Label>
										<Input
											type="number"
											min={1}
											style={{ width: 80 }}
											value={interval}
											onChange={(e) =>
												onChange({
													...data,
													recurrence: {
														...rec,
														interval: parseInt(e.target.value, 10) || 1,
													},
												})
											}
										/>
										<span>week(s) on:</span>
									</FormGroup>
									<FormGroup className="mb-3">
										{["MO", "TU", "WE", "TH", "FR", "SA", "SU"].map((wd) => (
											<FormGroup check inline key={wd}>
												<Input
													type="checkbox"
													id={`wd-${wd}`}
													checked={byWeekDays.includes(wd)}
													onChange={() => {
														const days = new Set(byWeekDays);
														days.has(wd) ? days.delete(wd) : days.add(wd);
														onChange({
															...data,
															recurrence: {
																...rec,
																byWeekDays: Array.from(days),
															},
														});
													}}
												/>
												<Label check className="ms-1" htmlFor={`wd-${wd}`}>
													{wd}
												</Label>
											</FormGroup>
										))}
									</FormGroup>
								</>
							)}

							{/* ── Monthly settings ─────────────────────── */}
							{freq === "MONTHLY" && (
								<FormGroup className="d-flex align-items-center gap-2 mb-3">
									<Label className="mb-0">Every</Label>
									<Input
										type="number"
										min={1}
										style={{ width: 80 }}
										value={interval}
										onChange={(e) =>
											onChange({
												...data,
												recurrence: {
													...rec,
													interval: parseInt(e.target.value, 10) || 1,
												},
											})
										}
									/>
									<span>month(s) on day</span>
									<Input
										type="number"
										min={1}
										max={31}
										style={{ width: 80 }}
										value={byMonthDay ?? ""}
										onChange={(e) =>
											onChange({
												...data,
												recurrence: {
													...rec,
													byMonthDay: parseInt(e.target.value, 10) || 1,
												},
											})
										}
									/>
								</FormGroup>
							)}

							{/* ── End-date picker ───────────────────────── */}
							{freq && (
								<FormGroup className="mb-3">
									<Label for="recurrence-end-date"><strong>End Date</strong></Label>
									<Flatpickr
										id="recurrence-end-date"
										className="form-control"
										options={{ dateFormat: "Y-m-d" }}
										value={endDate ?? undefined}
										onChange={([d]) =>
											onChange({
												...data,
												recurrence: { ...rec, endDate: d },
											})
										}
									/>
									<FormText color="muted">
										Optionally stop repeats on this date.
									</FormText>
								</FormGroup>
							)}

							{/* ── Show summary ─────────────────────────── */}
							{freq && (
								<FormText color="info">{recurrenceSummary}</FormText>
							)}
						</Col>
					</Row>
				</>
			)}
		</>
	);
};

export default StepSchedule;
